import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Youtube, Plus, Trash2, RefreshCw, Search, X, Loader2, Check, AlertTriangle, ChevronDown, ChevronUp, Info, ExternalLink, Film, Edit2 } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';

import { BACKEND_URL } from '../config/api';

function AddChannelModal({ onClose, onAdded }) {
  const [step, setStep] = useState('input'); // input, results, confirm
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [customName, setCustomName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isHandle = (val) => val.trim().startsWith('@') || val.includes('youtube.com');

  const handleLookup = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setSearchResults([]);

    if (isHandle(query)) {
      // Direct handle/URL resolve
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/youtube/resolve-channel?handle=${encodeURIComponent(query.trim())}`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error((await res.clone().json().catch(() => ({}))).detail || 'Could not resolve channel');
        const data = await res.clone().json();
        // Get thumbnail from resolve response
        setResolved({ ...data, thumbnail_url: data.thumbnail_url || null });
        setCustomName(data.channel_name || '');
        setDescription('');
        setStep('confirm');
      } catch (err) {
        setError(err.message);
      }
    } else {
      // Name search
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/youtube/search-channels?query=${encodeURIComponent(query.trim())}`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error('Search failed');
        const data = await res.clone().json();
        if (data.channels.length === 0) {
          setError('No channels found. Try a different name or use @handle.');
        } else {
          setSearchResults(data.channels);
          setStep('results');
        }
      } catch (err) {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  const selectChannel = (ch) => {
    setResolved({
      youtube_channel_id: ch.youtube_channel_id,
      channel_name: ch.name,
      thumbnail_url: ch.thumbnail_url,
      has_videos: true
    });
    setCustomName(ch.name);
    setDescription(ch.description || '');
    setStep('confirm');
  };

  const handleAdd = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/youtube/channels`, {
        method: 'POST', credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customName || resolved.channel_name || query,
          youtube_channel_id: resolved.youtube_channel_id,
          youtube_handle: isHandle(query) ? query : null,
          description: description || null
        })
      });
      if (!res.ok) throw new Error((await res.clone().json().catch(() => ({}))).detail || 'Failed to add channel');
      onAdded();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('input');
    setResolved(null);
    setSearchResults([]);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-beige flex-shrink-0">
          <h3 className="font-medium text-charcoal flex items-center gap-2">
            <Youtube size={18} className="text-red-500" />
            Add YouTube Channel
          </h3>
          <button onClick={onClose} className="text-gray-soft hover:text-charcoal" data-testid="add-channel-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {step === 'input' && (
            <>
              <div>
                <label className="text-sm font-medium text-charcoal block mb-1.5">Channel Name or Handle</label>
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Jennifer McGuire Ink or @JenniferMcGuireInk"
                  className="w-full px-3 py-2.5 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage"
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  data-testid="channel-handle-input"
                />
                <p className="text-xs text-gray-soft mt-1.5">
                  Type a <span className="font-medium">channel name</span> to search, or <span className="font-medium">@handle</span> / URL to look up directly
                </p>
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
            </>
          )}

          {step === 'results' && (
            <>
              <p className="text-sm text-gray-soft">Select a channel:</p>
              <div className="space-y-2">
                {searchResults.map(ch => (
                  <button
                    key={ch.youtube_channel_id}
                    onClick={() => selectChannel(ch)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-beige hover:border-sage hover:bg-sage-light/20 transition-all text-left"
                    data-testid={`search-result-${ch.youtube_channel_id}`}
                  >
                    {ch.thumbnail_url ? (
                      <img src={ch.thumbnail_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <Youtube size={18} className="text-red-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-charcoal truncate">{ch.name}</p>
                      {ch.subscriber_info && (
                        <p className="text-xs text-gray-soft">{ch.subscriber_info}</p>
                      )}
                      {ch.description && (
                        <p className="text-xs text-gray-soft mt-0.5 line-clamp-1">{ch.description}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 'confirm' && resolved && (
            <>
              {/* Channel preview card */}
              <div className="p-4 bg-cream/50 rounded-lg flex items-center gap-3 border border-beige">
                {resolved.thumbnail_url ? (
                  <img src={resolved.thumbnail_url} alt="" className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Youtube size={24} className="text-red-400" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-charcoal">{resolved.channel_name || customName}</p>
                  <p className="text-xs text-gray-soft font-mono">{resolved.youtube_channel_id}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-charcoal block mb-1.5">Display Name</label>
                <input
                  type="text"
                  value={customName}
                  onChange={e => setCustomName(e.target.value)}
                  className="w-full px-3 py-2 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage"
                  data-testid="channel-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-charcoal block mb-1.5">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Brief description of this channel's focus..."
                  className="w-full px-3 py-2 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage resize-none"
                  data-testid="channel-description-input"
                />
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-beige flex justify-end gap-3 flex-shrink-0">
          {(step === 'confirm' || step === 'results') && (
            <button onClick={goBack} className="px-4 py-2 text-sm text-gray-soft hover:text-charcoal transition-colors">Back</button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-soft hover:text-charcoal transition-colors">Cancel</button>
          {step === 'input' && (
            <button
              onClick={handleLookup}
              disabled={loading || !query.trim()}
              className="px-4 py-2 text-sm bg-charcoal text-white rounded-lg hover:bg-charcoal/90 disabled:opacity-50 flex items-center gap-2 transition-colors"
              data-testid="resolve-channel-btn"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {isHandle(query) ? 'Look Up' : 'Search'}
            </button>
          )}
          {step === 'confirm' && (
            <button
              onClick={handleAdd}
              disabled={loading || !customName.trim()}
              className="px-4 py-2 text-sm bg-charcoal text-white rounded-lg hover:bg-charcoal/90 disabled:opacity-50 flex items-center gap-2 transition-colors"
              data-testid="add-channel-btn"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Add Channel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EditChannelModal({ channel, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: channel.name || '',
    description: channel.description || '',
    enabled: channel.enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/youtube/channels/${channel.channel_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const d = await res.clone().json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to update channel');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-beige">
          <h3 className="font-medium text-charcoal flex items-center gap-2">
            <Youtube size={18} className="text-red-500" />
            Edit Channel
          </h3>
          <button onClick={onClose} className="text-gray-soft hover:text-charcoal" data-testid="edit-channel-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Channel ID (read-only) */}
          <div className="p-3 bg-cream/50 rounded-lg">
            <p className="text-xs text-gray-soft mb-0.5">YouTube Channel ID</p>
            <p className="text-sm text-charcoal font-mono">{channel.youtube_channel_id}</p>
            {channel.youtube_handle && (
              <p className="text-xs text-gray-soft mt-1">Handle: {channel.youtube_handle}</p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="text-sm font-medium text-charcoal block mb-1.5">Channel Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage"
              data-testid="edit-channel-name"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-charcoal block mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage resize-none"
              data-testid="edit-channel-description"
            />
          </div>

          {/* Enabled Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-charcoal">Enabled</p>
              <p className="text-xs text-gray-soft">Include in knowledge base</p>
            </div>
            <button
              onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.enabled ? 'bg-sage' : 'bg-beige'}`}
              data-testid="toggle-channel-enabled"
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.enabled ? 'translate-x-6' : 'translate-x-0.5'}`}>
                {form.enabled && <Check size={12} className="text-sage absolute top-1 left-1" />}
              </div>
            </button>
          </div>

          {/* Stats (read-only) */}
          <div className="flex gap-3 pt-2 border-t border-beige">
            <div className="flex-1 p-2 bg-cream/50 rounded-lg text-center">
              <p className="text-lg font-semibold text-charcoal">{channel.video_count || 0}</p>
              <p className="text-[10px] text-gray-soft">Videos</p>
            </div>
            <div className="flex-1 p-2 bg-cream/50 rounded-lg text-center">
              <p className="text-lg font-semibold text-charcoal">{channel.last_synced_at ? new Date(channel.last_synced_at).toLocaleDateString() : 'Never'}</p>
              <p className="text-[10px] text-gray-soft">Last Synced</p>
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-beige flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-soft hover:text-charcoal transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            className="px-4 py-2 text-sm bg-charcoal text-white rounded-lg hover:bg-charcoal/90 disabled:opacity-50 flex items-center gap-2 transition-colors"
            data-testid="save-channel-btn"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function IngestionLog({ channelId, onIngestionComplete }) {
  const [logs, setLogs] = useState([]);
  const [activeIngestions, setActiveIngestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const logContainerRef = useRef(null);
  const pollRef = useRef(null);
  const wasActiveRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const params = channelId ? `?channel_id=${channelId}` : '';
      const res = await fetch(`${BACKEND_URL}/api/admin/youtube/ingestion-status${params}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.clone().json();
        const nowActive = data.active_ingestions.length > 0;

        // Detect transition from active → done
        if (wasActiveRef.current && !nowActive && onIngestionComplete) {
          onIngestionComplete();
        }
        wasActiveRef.current = nowActive;

        setActiveIngestions(data.active_ingestions);
        setLogs(data.recent_logs);
      }
    } catch (err) {
      console.error('Failed to fetch ingestion status:', err);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  useEffect(() => {
    if (logContainerRef.current && activeIngestions.length > 0) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, activeIngestions]);

  const isActive = activeIngestions.length > 0;
  const latestLog = logs[0];

  return (
    <div className="bg-white rounded-xl border border-beige overflow-hidden" data-testid="ingestion-log">
      {/* Status Bar */}
      <div className={`px-4 py-3 flex items-center justify-between ${isActive ? 'bg-blue-50 border-b border-blue-100' : 'bg-cream/50 border-b border-beige'}`}>
        <div className="flex items-center gap-2">
          {isActive ? (
            <Loader2 size={16} className="animate-spin text-blue-600" />
          ) : (
            <Info size={16} className="text-gray-soft" />
          )}
          <span className={`text-sm font-medium ${isActive ? 'text-blue-800' : 'text-charcoal'}`}>
            {isActive ? 'Ingestion in progress...' : 'Ingestion Log'}
          </span>
        </div>
        {latestLog && !isActive && latestLog.completed_at && (
          <span className="text-xs text-gray-soft">
            Last: {new Date(latestLog.completed_at).toLocaleString()} — {latestLog.processed_videos || 0} videos, {latestLog.total_chunks || 0} chunks
          </span>
        )}
      </div>

      {/* Overall Progress */}
      {latestLog && (
        <div className="px-4 py-2 border-b border-beige">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-soft">{latestLog.channel_name}</span>
            <span className="text-charcoal font-medium">
              {(latestLog.processed_videos || 0) + (latestLog.skipped_videos || 0)} / {latestLog.total_videos || '?'}
            </span>
          </div>
          {latestLog.total_videos > 0 && (
            <div className="h-1.5 bg-beige rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  latestLog.status === 'completed' ? 'bg-green-500' :
                  latestLog.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(((latestLog.processed_videos + latestLog.skipped_videos) / latestLog.total_videos) * 100, 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Log Entries */}
      <div className="max-h-64 overflow-y-auto font-mono text-xs bg-gray-950 text-gray-300 p-3 space-y-0.5" data-testid="ingestion-log-entries" ref={logContainerRef}>
        {loading ? (
          <p className="text-gray-500">Loading logs...</p>
        ) : !latestLog || !latestLog.logs?.length ? (
          <p className="text-gray-500">No ingestion logs yet. Sync a channel to start.</p>
        ) : (
          latestLog.logs.map((entry, i) => {
            const levelColor = {
              info: 'text-blue-400',
              success: 'text-green-400',
              warning: 'text-yellow-400',
              error: 'text-red-400',
              skip: 'text-gray-500'
            }[entry.level] || 'text-gray-400';

            const time = entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : '';

            return (
              <div key={i} className="flex gap-2 leading-relaxed">
                <span className="text-gray-600 flex-shrink-0">{time}</span>
                <span className={`flex-shrink-0 w-12 ${levelColor}`}>[{entry.level?.toUpperCase()?.slice(0, 4)}]</span>
                <span className={entry.level === 'error' ? 'text-red-300' : ''}>{entry.message}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function AdminYouTube() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [expandedChannel, setExpandedChannel] = useState(null);
  const [channelVideos, setChannelVideos] = useState({});
  const [syncing, setSyncing] = useState({});
  const [editingChannel, setEditingChannel] = useState(null);
  const [syncMenuOpen, setSyncMenuOpen] = useState(null);
  const [syncMenuPos, setSyncMenuPos] = useState({ top: 0, right: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null); // { channelId, name }

  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/youtube/channels`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.clone().json();
        setChannels(data.channels);
      }
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/youtube/stats`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) setStats(await res.clone().json());
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => { fetchChannels(); fetchStats(); }, [fetchChannels, fetchStats]);

  const handleSync = async (channelId, limit = 3) => {
    setSyncMenuOpen(null);
    setSyncing(prev => ({ ...prev, [channelId]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/youtube/channels/${channelId}/sync?limit=${limit}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const d = await res.clone().json().catch(() => ({}));
        alert(d.detail || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
    } finally {
      // Keep syncing state for a bit while BG task starts
      setTimeout(() => setSyncing(prev => ({ ...prev, [channelId]: false })), 5000);
    }
  };

  const handleDelete = async (channelId, name) => {
    setDeleteTarget({ channelId, name });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/youtube/channels/${deleteTarget.channelId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      fetchChannels();
      fetchStats();
    } catch (err) {
      console.error('Delete error:', err);
    }
    setDeleteTarget(null);
  };

  const toggleExpand = async (channelId) => {
    if (expandedChannel === channelId) {
      setExpandedChannel(null);
      return;
    }
    setExpandedChannel(channelId);
    if (!channelVideos[channelId]) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/youtube/channels/${channelId}/videos`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const data = await res.clone().json();
          setChannelVideos(prev => ({ ...prev, [channelId]: data.videos }));
        }
      } catch (err) {
        console.error('Failed to fetch videos:', err);
      }
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-youtube">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-medium text-charcoal">Knowledge Base</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-charcoal text-white text-sm rounded-lg hover:bg-charcoal/90 transition-colors"
            data-testid="add-channel-btn"
          >
            <Plus size={16} />
            Add Channel
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Channels', value: stats.channels },
              { label: 'Videos', value: stats.total_videos },
              { label: 'Transcribed', value: stats.videos_with_transcripts },
              { label: 'Chunks', value: stats.total_chunks },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-lg border border-beige px-4 py-3" data-testid={`yt-stat-${s.label.toLowerCase()}`}>
                <p className="text-xl font-semibold text-charcoal">{s.value}</p>
                <p className="text-xs text-gray-soft">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Ingestion Log */}
        <div className="mb-6">
          <IngestionLog onIngestionComplete={() => { fetchChannels(); fetchStats(); }} />
        </div>

        {/* Channels List */}
        <div className="space-y-3">
          {loading ? (
            [1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-beige p-6 animate-pulse h-20" />)
          ) : channels.length === 0 ? (
            <div className="bg-white rounded-xl border border-beige p-12 text-center">
              <Youtube size={48} className="mx-auto mb-4 text-gray-soft" />
              <p className="text-charcoal font-medium mb-1">No channels added yet</p>
              <p className="text-sm text-gray-soft">Add YouTube channels to build your knowledge base</p>
            </div>
          ) : (
            channels.map(ch => (
              <div key={ch.channel_id} className="bg-white rounded-xl border border-beige overflow-hidden" data-testid={`channel-${ch.channel_id}`}>
                {/* Channel Header */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {ch.thumbnail_url ? (
                      <img src={ch.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                        <Youtube size={20} className="text-red-400" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-charcoal truncate">{ch.name}</p>
                        {!ch.enabled && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">Disabled</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-soft">{ch.video_count || 0} videos indexed</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingChannel(ch)}
                      className="p-2 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all"
                      title="Edit channel"
                      data-testid={`edit-channel-${ch.channel_id}`}
                    >
                      <Edit2 size={16} />
                    </button>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          if (syncMenuOpen === ch.channel_id) {
                            setSyncMenuOpen(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const menuHeight = 180; // approximate height of the dropdown
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const top = spaceBelow < menuHeight
                              ? rect.top - menuHeight - 4
                              : rect.bottom + 4;
                            setSyncMenuPos({ top, right: window.innerWidth - rect.right });
                            setSyncMenuOpen(ch.channel_id);
                          }
                        }}
                        disabled={syncing[ch.channel_id]}
                        className="p-2 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all disabled:opacity-50"
                        title="Sync videos"
                        data-testid={`sync-channel-${ch.channel_id}`}
                      >
                        <RefreshCw size={16} className={syncing[ch.channel_id] ? 'animate-spin' : ''} />
                      </button>
                    </div>
                    <button
                      onClick={() => handleDelete(ch.channel_id, ch.name)}
                      className="p-2 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete channel"
                      data-testid={`delete-channel-${ch.channel_id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      onClick={() => toggleExpand(ch.channel_id)}
                      className="p-2 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all"
                      data-testid={`expand-channel-${ch.channel_id}`}
                    >
                      {expandedChannel === ch.channel_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Videos */}
                {expandedChannel === ch.channel_id && (
                  <div className="border-t border-beige px-4 py-3 bg-cream/30">
                    {!channelVideos[ch.channel_id] ? (
                      <div className="flex items-center gap-2 text-gray-soft text-sm py-4 justify-center">
                        <Loader2 size={14} className="animate-spin" /> Loading videos...
                      </div>
                    ) : channelVideos[ch.channel_id].length === 0 ? (
                      <p className="text-sm text-gray-soft text-center py-4">No videos ingested yet. Click sync to fetch videos.</p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {channelVideos[ch.channel_id].map(v => (
                          <div key={v.video_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white transition-colors">
                            {v.thumbnail_url ? (
                              <img src={v.thumbnail_url} alt="" className="w-16 h-10 rounded object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-16 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Film size={14} className="text-gray-400" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-charcoal truncate">{v.title}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-soft">
                                <span>{v.published_at?.slice(0, 10)}</span>
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                                  v.transcript_status === 'completed' ? 'bg-green-50 text-green-600' :
                                  v.transcript_status === 'captions_disabled' ? 'bg-red-50 text-red-600' :
                                  v.transcript_status === 'ip_blocked' ? 'bg-orange-50 text-orange-600' :
                                  v.transcript_status === 'scheduled' ? 'bg-blue-50 text-blue-600' :
                                  v.transcript_status === 'no_captions' ? 'bg-gray-100 text-gray-500' :
                                  v.transcript_status === 'processing' ? 'bg-blue-50 text-blue-600' :
                                  v.transcript_status === 'unavailable' ? 'bg-gray-100 text-gray-500' :
                                  'bg-yellow-50 text-yellow-600'
                                }`}>
                                  {v.transcript_status === 'captions_disabled' ? 'captions off' :
                                   v.transcript_status === 'ip_blocked' ? 'IP blocked' :
                                   v.transcript_status === 'no_captions' ? 'no captions' :
                                   v.transcript_status || 'pending'}
                                </span>
                                {v.chunk_count > 0 && <span>{v.chunk_count} chunks</span>}
                              </div>
                            </div>
                            <a
                              href={`https://www.youtube.com/watch?v=${v.video_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-gray-soft hover:text-red-500 transition-colors flex-shrink-0"
                            >
                              <ExternalLink size={14} />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Channel Modal */}
        {showAddModal && (
          <AddChannelModal
            onClose={() => setShowAddModal(false)}
            onAdded={() => { fetchChannels(); fetchStats(); }}
          />
        )}

        {/* Edit Channel Modal */}
        {editingChannel && (
          <EditChannelModal
            channel={editingChannel}
            onClose={() => setEditingChannel(null)}
            onSaved={() => { fetchChannels(); fetchStats(); }}
          />
        )}

        {/* Sync Depth Menu (fixed position to avoid overflow clipping) */}
        {syncMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setSyncMenuOpen(null)} />
            <div
              className="fixed z-50 bg-white rounded-lg shadow-lg border border-beige py-1 w-44"
              style={{ top: syncMenuPos.top, right: syncMenuPos.right }}
              data-testid="sync-depth-menu"
            >
              <p className="px-3 py-1.5 text-[10px] font-medium text-gray-soft uppercase tracking-wide">Sync depth</p>
              {[3, 5, 10, 15].map(n => (
                <button
                  key={n}
                  onClick={() => handleSync(syncMenuOpen, n)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-cream transition-colors flex items-center justify-between"
                  data-testid={`sync-depth-${n}`}
                >
                  <span className="text-charcoal">Last {n} videos</span>
                  {n === 15 && <span className="text-[10px] text-gray-soft">RSS max</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Channel"
        message={`Delete "${deleteTarget?.name}" and all its videos & embeddings? This cannot be undone.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
        testId="delete-channel-dialog"
      />
    </AdminLayout>
  );
}
